import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Space, Spin, Dropdown, notification, Modal, Button, Typography } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { serverRequest } from "../../../util/server_request";
import { useLocation } from "react-router-dom";
import { PatternBackground } from "../../../components/PatternBackground";

const ScheduleMonth = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const month = queryParams.get('month');

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [originalData, setOriginalData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [memberDetailsLoading, setMemberDetailsLoading] = useState(false);
    const [memberDetails, setMemberDetails] = useState(null);

    const toggleLoading = () => setLoading(prevState => !prevState);

    const fetchData = useCallback(() => {
        toggleLoading();
        serverRequest('GET', `schedule/upload/months/detail?month=${month}`)
            .then(result => {
                setData(result.data || []);
                setOriginalData(result.data || []);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                toggleLoading();
            });
    }, [month]); // Dependencies: month

    useEffect(() => {
        fetchData();
    }, [fetchData]); // Now fetchData is properly memoized

    const columns = [
        {
            title: 'SSNIT No',
            dataIndex: 'member_no',
            key: 'member_no',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
            onFilter: (value, record) => (record.member_no || '').includes(value),
        },
        {
            title: 'Transaction Description',
            dataIndex: 'trans_desc',
            key: 'trans_desc',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Total Contribution',
            dataIndex: 'TotalContribution',
            key: 'TotalContribution',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Employer Contribution',
            dataIndex: 'EmployerContribution',
            key: 'EmployerContribution',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Employee Contribution',
            dataIndex: 'EmployeeContribution',
            key: 'EmployeeContribution',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Monthly Salary',
            dataIndex: 'MonthlySalary',
            key: 'MonthlySalary',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Payroll Year',
            dataIndex: 'PayrollYear',
            key: 'PayrollYear',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Payroll Month',
            dataIndex: 'PayrollMonth',
            key: 'PayrollMonth',
            width: 50,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>,
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button
                    type="link"
                    
                    onClick={() => handleViewDetails(record)}
                    style={{ color: '#52c41a', fontWeight: 'bold' }}
                >
                    View
                </Button>
            ),
        }
    ];

    // Function to fetch member details
    const fetchMemberDetails = async (memberNumber) => {
        setMemberDetailsLoading(true);
        try {
            const result = await serverRequest('GET', `members/details?ssnit-number=${memberNumber}&staff-number=${memberNumber}`);
            setMemberDetails(result.data || {});
        } catch (error) {
            console.error('Error fetching member details:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to fetch member details. Please try again.'
            });
            setMemberDetails(null);
        } finally {
            setMemberDetailsLoading(false);
        }
    };

    // Function to handle view details
    const handleViewDetails = (record) => {
        setSelectedMember(record);
        setModalVisible(true);
        if (record.member_no) {
            fetchMemberDetails(record.member_no);
        }
    };

    // Function to handle modal close
    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedMember(null);
        setMemberDetails(null);
    };
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);

        const filteredData = originalData.filter(item => 
            Object.values(item).some(val => 
                String(val || '').toLowerCase().includes(value)
            )
        );
        setData(filteredData);
    };

    const exportToCSV = async (data, fileName) => {
        setExportLoading(true);
        try {
            const { utils, writeFile } = await import('xlsx');
            
            const cleanData = data.map(item => {
                const cleanItem = {};
                Object.keys(item).forEach(key => {
                    cleanItem[key] = item[key] ?? '';
                });
                return cleanItem;
            });

            const worksheet = utils.json_to_sheet(cleanData);
            const workbook = utils.book_new();
            utils.book_append_sheet(workbook, worksheet, 'Sheet1');
            
            writeFile(workbook, `${fileName}_${new Date().toISOString().slice(0, 10)}.csv`, {
                bookType: 'csv',
                type: 'array'
            });
        } catch (error) {
            console.error('CSV export error:', error);
            notification.error({
                message: 'Export Failed',
                description: 'Failed to generate CSV file'
            });
        } finally {
            setExportLoading(false);
        }
    };

    const exportToPDF = async (columns, data, title) => {
        setExportLoading(true);
        try {
            const { jsPDF } = await import('jspdf');
            await import('jspdf-autotable'); // Import for side effects
            
            const doc = new jsPDF();
            const headers = columns.map(col => col.title);
            const rows = data.map(row => 
                columns.map(col => row[col.dataIndex] ?? '')
            );

            doc.autoTable({
                head: [headers],
                body: rows,
                theme: 'grid',
                styles: { overflow: 'linebreak' },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 30 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 20 },
                    7: { cellWidth: 20 }
                }
            });

            doc.save(`${title}_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            console.error('PDF export error:', error);
            notification.error({
                message: 'Export Failed',
                description: 'Failed to generate PDF file'
            });
        } finally {
            setExportLoading(false);
        }
    };

    const exportFunction = (type) => {
        if (!data || data.length === 0) {
            notification.warning({
                message: 'No Data',
                description: 'There is no data to export'
            });
            return;
        }

        if (type === 'pdf') {
            exportToPDF(columns, data, 'Contributions Report');
        } else {
            exportToCSV(data, 'contributions');
        }
    };

    return (
        <div>
            <PatternBackground>
                <p className="lead fw-bold">All Contributions</p>
                <small>View all contributions</small>
            </PatternBackground>
            
            <div className="om-container">
                <div className='p-4 shadow-sm bg-white rounded'>
                    <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'csv',
                                        label: 'Export as CSV',
                                        onClick: () => exportFunction('csv'),
                                        disabled: exportLoading || data.length === 0
                                    },
                                    {
                                        key: 'pdf',
                                        label: 'Export as PDF',
                                        onClick: () => exportFunction('pdf'),
                                        disabled: exportLoading || data.length === 0
                                    }
                                ]
                            }}
                            disabled={exportLoading || data.length === 0}
                        >
                            <button 
                                className="btn btn-primary"
                                disabled={exportLoading || data.length === 0}
                            >
                                <DownloadOutlined style={{ marginRight: '8px' }} />
                                EXPORT
                            </button>
                        </Dropdown>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="SEARCH"
                            style={{ width: 200 }}
                            value={searchText}
                            onChange={handleSearch}
                            allowClear
                        />
                    </Space>

                    <Spin spinning={loading}>
                        <Table
                            columns={columns}
                            dataSource={data}
                            pagination={{ pageSize: 10 }}
                            rowKey={(record) => record.member_no || Math.random()}
                            scroll={{ x: true }}
                        />
                    </Spin>
                </div>

                {/* Member Details Modal */}
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>Member Details</span>
                        </div>
                    }
                    open={modalVisible}
                    onCancel={handleModalClose}
                    footer={[]}
                    width={800}
                    loading={memberDetailsLoading}
                >
                    {selectedMember && (
                        <div>
                            <Typography.Title level={5} style={{ marginBottom: '20px', color: '#000', fontSize: '16px' }}>
                                Contribution Details for {memberDetails && memberDetails.member_no ? memberDetails.member_no : 'N/A'}
                            </Typography.Title>
                            {/* Basic Contribution Info */}
                            <div style={{ marginBottom: '30px' }}>
                                <Typography.Title level={6} style={{ 
                                    marginBottom: '15px', 
                                    borderBottom: '1px solid #e0e0e0', 
                                    paddingBottom: '8px',
                                    color: '#000',
                                    fontSize: '14px'
                                }}>
                                    Contribution Information
                                </Typography.Title>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                                    gap: '15px',
                                    padding: '0 10px'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Total Contribution:</strong>
                                            <span>{selectedMember.TotalContribution || 'N/A'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Employee Contribution:</strong>
                                            <span>{selectedMember.EmployeeContribution || 'N/A'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Payroll Year:</strong>
                                            <span>{selectedMember.PayrollYear || 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Transaction Description:</strong>
                                            <span>{selectedMember.trans_desc || 'N/A'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Employer Contribution:</strong>
                                            <span>{selectedMember.EmployerContribution || 'N/A'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Monthly Salary:</strong>
                                            <span>{selectedMember.MonthlySalary || 'N/A'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                            <strong>Payroll Month:</strong>
                                            <span>{selectedMember.PayrollMonth || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Member Details from API */}
                            {memberDetails && (
                                <div>
                                    <Typography.Title level={6} style={{ 
                                        marginBottom: '15px', 
                                        borderBottom: '1px solid #e0e0e0', 
                                        paddingBottom: '8px',
                                        color: '#000',
                                        fontSize: '14px'
                                    }}>
                                        Member Information
                                    </Typography.Title>
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                                        gap: '15px',
                                        padding: '0 10px'
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Member No:</strong>
                                                <span>{memberDetails.member_no || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Full Name:</strong>
                                                <span>{memberDetails.FullName || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Date of Birth:</strong>
                                                <span>{memberDetails.dob ? new Date(memberDetails.dob).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Gender:</strong>
                                                <span>{memberDetails.Sex || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Monthly Salary:</strong>
                                                <span>{memberDetails.monthly_salary || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Monthly Contribution:</strong>
                                                <span>{memberDetails.MonthlyContribution || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>SSNIT Number:</strong>
                                                <span>{memberDetails.ssnit_number || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Mobile Number:</strong>
                                                <span>{memberDetails.mobile_no || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Email:</strong>
                                                <span style={{ wordBreak: 'break-all' }}>{memberDetails.email || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Employer Number:</strong>
                                                <span>{memberDetails.EmployerNumber || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Employer Name:</strong>
                                                <span>{memberDetails.EmployerName || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Pension Type ID:</strong>
                                                <span>{memberDetails.pension_type_id || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Pension Type Name:</strong>
                                                <span>{memberDetails.pension_type_name || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Scheme ID:</strong>
                                                <span>{memberDetails.scheme_id || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Scheme Name:</strong>
                                                <span>{memberDetails.scheme_name || 'N/A'}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                                <strong>Ghana Card Number:</strong>
                                                <span>{memberDetails.GIN || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {memberDetailsLoading && (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: '10px' }}>Loading member details...</div>
                                </div>
                            )}
                            {!memberDetailsLoading && !memberDetails && (
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '20px',
                                    color: '#ff4d4f'
                                }}>
                                    Failed to load member details. Please try again.
                                </div>
                            )}
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
};

export default ScheduleMonth;
